import React, {CSSProperties, MutableRefObject, ReactNode, RefObject, forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react'
import ReactDOM from 'react-dom'

import Colors from '@utils/Colors.js'
import {shadows, zIndex} from '../styles.js'
import {Overlay, closeOverlay, openOverlay} from './overlayStack.js'

// Keeps a panel that has no room at either side this far from the viewport edge
const viewportMargin = 8

export interface PopoverProps {
    /** The anchor: clicking it opens the panel (unless noClickOpen). Without children, anchorRef or inputRef is the anchor */
    children?: ReactNode
    /** The panel's content */
    content?: ReactNode
    /** Default: below the anchor, left aligned. 'left': right aligned, 'top-left': above it, right aligned */
    placement?: string
    /** The anchor when there are no children, read when placing */
    anchorRef?: RefObject<HTMLElement> | MutableRefObject<HTMLElement | null>
    /** The anchor element when there are no children and no anchorRef */
    inputRef?: HTMLElement | null
    /** The panel is not wider than the anchor */
    fitAnchorWidth?: boolean
    /** A disabled Popover doesn't show its panel */
    disabled?: boolean
    /** Clicking in the panel doesn't close it */
    noClose?: boolean
    /** Clicking the anchor doesn't open the panel, the caller opens it with show() */
    noClickOpen?: boolean
    /** Escape passes the Popover (a hover tooltip isn't something the user opened to work in) */
    ignoreEscape?: boolean
    /** Called with the new open state when it opens or closes */
    onToggle?: (open: boolean) => void
    /** Called when a mousedown outside the open panel closes it */
    onOutsideClick?: () => void
    /** Style of the panel; properties set here are not changed by the placement */
    popoverStyle?: CSSProperties
    /** Style of the wrapper around children */
    style?: CSSProperties
}

/** The imperative API of Popover, the type of its ref (useRef<PopoverRef>) */
export interface PopoverRef {
    show: () => void
    /** outsideClick: closed by a mousedown outside the panel, calls onOutsideClick when it was open */
    close: (event?: unknown, outsideClick?: boolean) => void
    /** Whether the panel is closed */
    closed: () => boolean
}

// Whether the anchor is visible: rendered, in the viewport and not scrolled out of an overflow container around it.
// A panel whose anchor is scrolled out of view is hidden instead of floating over other content.
function isAnchorVisible(anchor: HTMLElement, rect: DOMRect, viewportWidth: number, viewportHeight: number) {
    if (!anchor.getClientRects().length) {
        return false
    }

    const outside = (box: {top: number, left: number, bottom: number, right: number}) => rect.bottom < box.top || rect.top > box.bottom || rect.right < box.left || rect.left > box.right

    if (outside({top: 0, left: 0, bottom: viewportHeight, right: viewportWidth})) {
        return false
    }

    let node = anchor.parentElement

    while (node && node !== document.body) {
        const style = window.getComputedStyle(node)

        if ((style.overflowX !== 'visible' || style.overflowY !== 'visible') && outside(node.getBoundingClientRect())) {
            return false
        }

        if (style.position === 'fixed') {
            break
        }

        node = node.parentElement
    }

    return true
}

/**
 * A panel (menu, option list, calendar, info text) that opens at an anchor element.
 *
 * The anchor is the wrapper around children (clicking it opens the panel unless noClickOpen), or anchorRef (a ref
 * object, read when placing) / inputRef (an element) when the Popover has no children; then nothing is rendered in
 * place, so it adds no element to the layout of the component it is used in. The wrapper is a plain block (position
 * relative, plus the style prop): it never looks at the children's style, pass style when the wrapper itself has to
 * flex or have a width in its parent.
 *
 * The panel is rendered in a portal on document.body with position fixed and placed from the anchor's
 * getBoundingClientRect, so overflow hidden/scroll containers and modals don't clip it. It is placed again after
 * every render, on scroll (of any container) and on resize while open, and hidden while its anchor is scrolled out
 * of view. It is at least as wide as the anchor and opens below it, left aligned (placement 'left': right aligned,
 * 'top-left': above it, right aligned, 'right': at the right of it, top aligned and moved up when it doesn't fit
 * below). When there is no room below and more room above, it opens above the anchor.
 *
 * React events in the panel still bubble to the Popover's React parents (portal), but the panel stops click and
 * mousedown. Mousedown outside the panel closes it (and calls onOutsideClick), clicking in it closes it unless
 * noClose. Escape closes the topmost open overlay only (overlayStack.ts): a Popover opened from another one's panel
 * closes before it, a Modal around it stays open. A disabled or hidden Popover and one with ignoreEscape (a hover
 * tooltip) let Escape through.
 *
 * Imperative (PopoverRef): show(), close(), closed().
 */
const Popover = forwardRef<PopoverRef, PopoverProps>(function Popover(props, ref) {
    const {children, content, popoverStyle = {}, style, disabled, noClose, noClickOpen, onToggle} = props
    const [show, setShow] = useState(false)

    // The listeners outlive renders, so they read the latest props and state through refs
    const propsRef = useRef(props)
    propsRef.current = props
    const showRef = useRef(show)
    showRef.current = show

    const popoverEl = useRef<HTMLDivElement>(null)
    const child = useRef<HTMLDivElement>(null)
    const listening = useRef(false)

    // Placed on the element directly instead of through state, so scrolling doesn't re-render the content. Only sets
    // properties popoverStyle doesn't, React keeps those.
    const updatePosition = useRef(() => {
        const {placement, fitAnchorWidth, disabled, popoverStyle = {}, children, anchorRef, inputRef} = propsRef.current
        const el = popoverEl.current
        const anchor = anchorRef ? anchorRef.current : children ? child.current : inputRef

        if (!el || !anchor || !showRef.current || disabled) {
            return
        }

        const set = (property: 'top' | 'left' | 'bottom' | 'right' | 'maxHeight' | 'overflowY' | 'minWidth' | 'maxWidth', value: number | string) => {
            if (popoverStyle[property] === undefined) {
                el.style[property] = typeof value === 'number' ? `${value}px` : value
            }
        }

        const rect = anchor.getBoundingClientRect()
        const viewportWidth = document.documentElement.clientWidth
        const viewportHeight = document.documentElement.clientHeight

        // Measure at the top left, where the panel has the full viewport width
        set('top', 0)
        set('left', 0)
        set('bottom', '')
        set('right', '')
        set('maxHeight', '')
        set('overflowY', '')
        set('minWidth', rect.width)
        set('maxWidth', fitAnchorWidth ? rect.width : '')

        const width = el.offsetWidth
        const height = el.offsetHeight

        if (placement === 'right') {
            // Next to the anchor, top aligned, moved up as far as needed to stay in the viewport (a menu opened from
            // the bottom of the side nav)
            set('left', Math.min(rect.right, viewportWidth - width))
            set('top', Math.max(viewportMargin, Math.min(rect.top, viewportHeight - viewportMargin - height)))

            if (height > viewportHeight - 2 * viewportMargin) {
                set('maxHeight', viewportHeight - 2 * viewportMargin)
                set('overflowY', 'auto')
            }

            el.style.visibility = isAnchorVisible(anchor, rect, viewportWidth, viewportHeight) ? '' : 'hidden'
            return
        }

        let left = placement === 'left' || placement === 'top-left' ? rect.right - width : rect.left
        left = Math.max(0, Math.min(left, viewportWidth - width))
        set('left', left)

        if (placement === 'top-left') {
            set('top', '')
            set('bottom', viewportHeight - rect.bottom + 25)
        } else {
            const spaceBelow = viewportHeight - rect.bottom
            const spaceAbove = rect.top
            const above = height > spaceBelow && spaceAbove > spaceBelow

            if (above) {
                // From the bottom, so it stays against the anchor when its content gets shorter or longer
                set('top', '')
                set('bottom', viewportHeight - rect.top)
            } else {
                set('top', rect.bottom)
            }

            const space = above ? spaceAbove : spaceBelow
            if (height > space) {
                set('maxHeight', Math.max(space - viewportMargin, 100))
                set('overflowY', 'auto')
            }
        }

        el.style.visibility = isAnchorVisible(anchor, rect, viewportWidth, viewportHeight) ? '' : 'hidden'
    }).current

    const onScroll = useRef((event: Event) => {
        // Scrolling a list inside the panel doesn't move the anchor
        if (popoverEl.current && event.target instanceof Node && popoverEl.current.contains(event.target)) {
            return
        }

        updatePosition()
    }).current

    const api = useRef<PopoverRef & {overlay: Overlay, removePositionListeners: () => void} | null>(null)

    if (!api.current) {
        const removePositionListeners = () => {
            window.removeEventListener('scroll', onScroll, true)
            window.removeEventListener('resize', updatePosition)
            listening.current = false
        }

        const close: PopoverRef['close'] = (event, outsideClick) => {
            if (outsideClick && showRef.current) {
                propsRef.current.onOutsideClick?.()
            }

            removePositionListeners()
            closeOverlay(overlay)

            // Bails out of the render when closed already: every mousedown on the page closes every Popover
            setShow(false)
        }

        // A disabled Popover doesn't show its panel, one whose anchor is scrolled out of view hides it, and a hover
        // tooltip (ignoreEscape) isn't something the user opened to work in
        const overlay: Overlay = {
            kind: 'popover',
            onEscape: () => close(),
            takesEscape: () => !propsRef.current.disabled && !propsRef.current.ignoreEscape && popoverEl.current?.style.visibility !== 'hidden'
        }

        api.current = {
            overlay,
            removePositionListeners,
            close,
            closed: () => !showRef.current,
            show: () => {
                if (!listening.current) {
                    // Capture, so scrolling any container (a modal, a panel) moves the panel along with its anchor
                    window.addEventListener('scroll', onScroll, true)
                    window.addEventListener('resize', updatePosition)
                    listening.current = true
                }

                openOverlay(overlay)

                setShow(true)
            }
        }
    }

    const {show: showPanel, close} = api.current

    useImperativeHandle(ref, () => ({show: api.current.show, close: api.current.close, closed: api.current.closed}), [])

    useEffect(() => {
        const outsideClickClose = (event: MouseEvent) => close(event, true)

        window.addEventListener('mousedown', outsideClickClose)

        return () => {
            window.removeEventListener('mousedown', outsideClickClose)
            api.current.removePositionListeners()
            closeOverlay(api.current.overlay)
        }
    }, [])

    // Placed again after every render while open, before the browser paints
    useLayoutEffect(() => {
        if (show) {
            updatePosition()
        }
    })

    // Not on mount, like componentDidUpdate before
    const prevShow = useRef(show)
    useLayoutEffect(() => {
        if (prevShow.current !== show) {
            prevShow.current = show

            if (typeof onToggle === 'function') {
                onToggle(show)
            }
        }
    }, [show])

    const isOpen = !disabled && show

    // top/left/bottom/right/minWidth/maxWidth/maxHeight are set by updatePosition
    const panelStyle: CSSProperties = {
        display: isOpen ? 'block' : 'none',
        // Above Modal and the map controls, below ToolTip
        zIndex: zIndex.popover,
        whiteSpace: 'nowrap',
        width: 'auto',
        background: 'white',
        border: isOpen ? `1px solid ${Colors.grey20}` : 'none',
        boxShadow: isOpen ? shadows.popover : '',
        ...popoverStyle,
        position: 'fixed'
    }

    const panel = (
        <div
            style={panelStyle}
            onClick={(event) => {
                event.stopPropagation()
                if (!noClose) {
                    close()
                }
            }}
            onMouseDown={(event) => event.stopPropagation()}
            ref={popoverEl}
        >
            {content}
        </div>
    )

    const portal = typeof document !== 'undefined' ? ReactDOM.createPortal(panel, document.body) : null

    if (!children) {
        return portal
    }

    return (
        <div style={{position: 'relative', ...style}} onClick={noClickOpen ? null : showPanel} ref={child}>
            {children}

            {portal}
        </div>
    )
})

export default Popover
